import { Menu, Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  HeartIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import { useFetcher } from "@remix-run/react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { twJoin, twMerge } from "tailwind-merge";
import LoginForm from "./LoginForm";
import { useProfile } from "./ProfileProvider";
import toast from "react-hot-toast";
import ModalCreateMoodboard from "./ModalCreateMoodboard";

type Props = { id: string; className?: string };
type FetcherResponse = {
  ok?: true;
  removed?: boolean;
  id?: string;
  moodboardId?: string;
  error?: string;
};

export default function BookmarkButton(props: Props) {
  const { auth, bookmarks, moodboards } = useProfile();
  const fetcher = useFetcher<FetcherResponse>();
  const [saved, setSaved] = useState(bookmarks?.some((b) => b.id === props.id));
  const [showLogin, setShowLogin] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    setSaved(bookmarks.some((b) => b.id === props.id));
  }, [bookmarks]);

  useEffect(() => {
    if (!fetcher.data) return;
    if (fetcher.data.error === "not-authenticated") {
      setShowLogin(true);
      return;
    }
    const moodboardId = fetcher.data.moodboardId;
    if (!moodboardId) return;
    if (fetcher.data.ok) {
      const title = moodboards.find((m) => m.id === moodboardId)?.title;
      toast.success(
        fetcher.data.removed
          ? `Item removed from '${title}' moodboard`
          : `Item added to '${title}' moodboard`,
        { id: "bookmark" },
      );
    }
    if (fetcher.data.removed) {
      const idx = bookmarks.findIndex((b) => {
        return b.moodboardId === moodboardId && b.id === props.id;
      });
      bookmarks.splice(idx, 1);
      setSaved(false);
    } else {
      bookmarks.push({ id: props.id, moodboardId });
      setSaved(true);
    }
  }, [fetcher.data]);

  const toggleBookmark = useCallback((moodboardId: string) => {
    const form = new FormData();
    form.append("intent", "bookmark");
    form.append("id", props.id);
    fetcher.submit(form, {
      action: `/account/moodboards/${moodboardId}`,
      method: "POST",
      preventScrollReset: true,
    });
  }, []);

  return (
    <Menu
      as="div"
      className={twMerge("absolute right-2 top-2 z-10", props.className)}
    >
      <Menu.Button
        data-tooltip-id="tooltip"
        data-tooltip-content="Add item to Moodboard"
        data-tooltip-class-name="!text-xs"
        type="button"
        className="relative z-0 text-gray-500 opacity-80 mix-blend-multiply"
        onClick={(e) => {
          if (!auth) {
            e.preventDefault();
            setShowLogin(true);
          }
        }}
      >
        {saved ? (
          <HeartIconSolid className="size-5" />
        ) : (
          <HeartIcon className="size-5" />
        )}
      </Menu.Button>
      <Transition
        as={Fragment}
        enterFrom="opacity-0 -translate-y-4"
        leaveTo="opacity-0 -translate-y-4"
      >
        <Menu.Items
          className={twJoin(
            "absolute right-0 mt-2 w-56",
            "origin-top-right divide-y divide-gray-100",
            "overflow-hidden rounded bg-white shadow-lg",
            "ring-1 ring-black/5 focus:outline-none",
            "transition",
          )}
        >
          <h3 className="p-4 text-xs uppercase tracking-widest">
            Add to Moodboard
          </h3>
          {[
            ...moodboards,
            { id: "new", title: "Create new Moodboard", createdAt: 0 },
          ].map((m) => (
            <Menu.Item key={m.id}>
              {({ active }) => (
                <button
                  className={twJoin(
                    "flex w-full items-center justify-between",
                    "p-4 text-sm",
                    active && "bg-neutral-100",
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    if (m.id === "new") {
                      setShowCreateModal(true);
                    } else {
                      toggleBookmark(m.id);
                    }
                  }}
                >
                  <span className="truncate">{m.title}</span>
                  {m.id === "new" ? (
                    <PlusCircleIcon className="size-5 text-neutral-400/80" />
                  ) : (
                    <CheckCircleIcon
                      className={twJoin(
                        "size-5 stroke-2",
                        bookmarks.some(
                          (b) => b.moodboardId === m.id && b.id === props.id,
                        )
                          ? "text-green-600"
                          : "text-neutral-400/80",
                      )}
                    />
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
      <ModalCreateMoodboard
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
      <LoginForm
        open={showLogin}
        onClose={() => setShowLogin(false)}
        onAuth={() => setShowLogin(false)}
      />
    </Menu>
  );
}
